import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { DrawerBody } from 'tio-ui/components/drawer';
import Chip from 'tio-ui/components/chip';
import { formatTransactionAmount } from '../../../utils/course-cash/format-transaction';
import type { PcsCardExtension as PcsCardModel } from 'tio-data/data/resources/pcs-card.ext';
import type { PcsAuthorization as PcsAuthorizationModel } from 'tio-data/data/resources/pcs-authorization.type';
import type { PcsCardEvent as PcsCardEventModel } from 'tio-data/data/resources/pcs-card-event.type';
import type { PcsTransaction as PcsTransactionModel } from 'tio-data/data/resources/pcs-transaction.type';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { findRecord } from '@warp-drive/utilities/json-api';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import { PhCheckCircle } from 'ember-phosphor-icons';
import { reactivateTasAssistanceRequest } from 'tio-data/requests/tas-assistance';
import type { Store } from '@warp-drive/core';
import type { TasAssistanceExtension as TasAssistanceModel } from 'tio-data/data/resources/tas-assistance.ext';
import type SessionContextService from '../../../services/session-context';

const EPOCH_ISO = '1970-01-01T00:00:00.000Z';

// Injected by name at runtime (reimagined-toast). Typed structurally to avoid pulling
// tio-ui's notifications service registry into tio-common's type graph (they collide — TS2717).
interface ReactivateToast {
  success: (message: string) => unknown;
  error: (message: string) => unknown;
}

interface ReactivationAudit {
  by: string;
  card: string;
  at: string;
}

interface ActivityEntry {
  id: string;
  date: string;
  merchantName: string;
  referenceNumber: string;
  amount: number;
  status:
    | 'approved'
    | 'declined'
    | 'captured'
    | 'refunded'
    | 'reversed'
    | 'created'
    | 'reactivated'
    | 'deactivated';
  declineReason: string;
  isLast: boolean;
}

interface ActivityViewSignature {
  Args: {
    card: PcsCardModel;
    cardNumber?: number;
    totalCards?: number;
    showReferenceNumber?: boolean;
    // The deactivated course cash reserve eligible for the one-time reactivation.
    // When present (and the card is inactivated) the reactivate button is shown.
    reactivatableAssistance?: TasAssistanceModel;
  };
}

export default class ActivityView extends Component<ActivityViewSignature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service('reimagined-toast') declare toast: ReactivateToast;
  @service declare sessionContext: SessionContextService;

  @tracked showReactivateConfirm = false;
  @tracked reactivateError: string | null = null;
  @tracked reactivationAudit: ReactivationAudit | null = null;
  @tracked reactivateComment = '';

  // A reason is required; it is saved to the reserve's transition_log and PaperTrail.
  get isReactivateCommentEmpty() {
    return this.reactivateComment.trim().length === 0;
  }

  updateReactivateComment = (event: Event) => {
    this.reactivateComment = (event.target as HTMLTextAreaElement).value;
  };

  /**
   * Show the reactivate button only for an inactivated (reversible) card that still
   * has a reactivatable reserve. A canceled card is terminal, so it is excluded.
   */
  get showReactivateButton() {
    return (
      this.args.card.state === 'PCS.CardState.INACTIVATED' &&
      !!this.args.reactivatableAssistance &&
      !this.args.reactivatableAssistance.previouslyReactivated
    );
  }

  // Card detail summary shown in the confirmation dialog so the admin can verify
  // they are acting on the right card.
  get cardholderName() {
    return (
      this.args.card.pcsCardHolder?.legalName ??
      this.intl.t('tuition_assistance.course_cash.activity.unknown')
    );
  }

  get programName() {
    return this.args.card.pcsProgram?.description ?? this.args.card.program ?? '';
  }

  get cardLastFour() {
    return (this.args.card.providerData?.last4 as string) ?? '';
  }

  openReactivateConfirm = () => {
    this.reactivateError = null;
    this.reactivateComment = '';
    this.showReactivateConfirm = true;
  };

  cancelReactivateConfirm = () => {
    this.showReactivateConfirm = false;
  };

  /**
   * Fire the one-time reactivation on the backing reserve once the admin confirms.
   * The server restores the reserve to RESERVED and re-activates the card synchronously,
   * emitting a CARD_REACTIVATED card event. We re-fetch the card WITH its pcs-card-events
   * sideloaded (not a bare reload) so the new event lands in the store and the timeline
   * picks it up in place: pcsCardEvents is an `async: false` relationship, so a plain
   * card.reload() refreshes the card's own attributes (state → the chip flips, the button
   * hides) but never re-pulls the hasMany — without the include the new "Card Reactivated"
   * row stays invisible until a full page reload. On success we record an audit entry
   * (who / which card / when) for accountability.
   */
  handleReactivate = dropTask(async () => {
    // Clear any prior failure so a retry from the still-open dialog starts clean
    // (otherwise the stale error banner lingers over the new attempt).
    this.reactivateError = null;
    const assistanceId = this.args.reactivatableAssistance?.id;
    const cardId = this.args.card.id;
    if (!assistanceId || !cardId) return;

    try {
      await this.store.request(
        reactivateTasAssistanceRequest(assistanceId, this.reactivateComment.trim())
      );
      await Promise.all([
        this.store.request({
          ...findRecord<PcsCardModel>('pcs-card', cardId, { include: ['pcs-card-events'] }),
          cacheOptions: { reload: true },
        }),
        this.args.reactivatableAssistance?.reload(),
      ]);
      this.reactivationAudit = {
        by: this.currentUserName,
        card: this.cardLastFour,
        at: this.intl.formatDate(new Date(), {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
      };
      this.showReactivateConfirm = false;
      this.toast.success(this.intl.t('tuition_assistance.course_cash.activity.reactivate_success'));
    } catch {
      this.reactivateError = this.intl.t(
        'tuition_assistance.course_cash.activity.reactivate_error'
      );
      this.toast.error(this.intl.t('tuition_assistance.course_cash.activity.reactivate_error'));
    }
  });

  private get currentUserName() {
    const person = this.sessionContext.user?.person;
    const full = [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim();
    return full || this.intl.t('tuition_assistance.course_cash.activity.unknown');
  }

  get cardStatus() {
    const state = this.args.card.state;
    if (state === 'PCS.CardState.ACTIVATED') return 'active';
    if (state === 'PCS.CardState.INACTIVATED') return 'inactive';
    if (state === 'PCS.CardState.CANCELED') return 'canceled';
    if (state === 'PCS.CardState.CREATED') return 'created';
    return 'unknown';
  }

  private get resolvedCardNumber() {
    return this.args.cardNumber ?? 1;
  }

  private get resolvedTotalCards() {
    return this.args.totalCards ?? 1;
  }

  get isOneOfMultiple() {
    return this.resolvedTotalCards > 1;
  }

  get cardStatusIntent() {
    if (this.cardStatus === 'active') return 'success';
    if (this.cardStatus === 'inactive' || this.cardStatus === 'canceled') return 'danger';
    if (this.cardStatus === 'created') return 'warning';
    return 'default';
  }

  get cardStatusLabel() {
    const status = this.cardStatus;
    const keys: Record<string, string> = {
      active: 'tuition_assistance.course_cash.activity.card_status_active',
      inactive: 'tuition_assistance.course_cash.activity.card_status_inactive',
      canceled: 'tuition_assistance.course_cash.activity.card_status_canceled',
      created: 'tuition_assistance.course_cash.activity.card_status_created',
      unknown: 'tuition_assistance.course_cash.activity.card_status_unknown',
    };
    const key = keys[status] ?? 'tuition_assistance.course_cash.activity.card_status_unknown';
    return this.intl.t(key);
  }

  get activityTimeline(): ActivityEntry[] {
    const card = this.args.card;
    const authorizations = card.pcsAuthorizations ?? [];

    const entries: ActivityEntry[] = [];

    for (const auth of authorizations as PcsAuthorizationModel[]) {
      const providerData = auth.providerData as Record<string, unknown> | undefined;
      const merchantData = providerData?.merchant_data as Record<string, string> | undefined;
      const merchantName =
        merchantData?.name ??
        this.intl.t('tuition_assistance.course_cash.activity.unknown_merchant');
      const networkId = merchantData?.network_id ?? auth.providerId ?? '';

      const providerCreated = providerData?.created;
      const baseDate =
        typeof providerCreated === 'string' && providerCreated.length > 0
          ? this.normalizeToUtc(providerCreated)
          : auth.createdAt || EPOCH_ISO;

      const transitionLog = auth.transitionLog ?? [];
      const relevantTransitions = transitionLog.filter((log) => {
        const to = log.to ?? '';
        return (
          to.includes('APPROVED') ||
          to.includes('CAPTURED') ||
          to.includes('DECLINED') ||
          to.includes('REVERSED')
        );
      });

      if (relevantTransitions.length > 0) {
        for (const logEntry of relevantTransitions) {
          const toState = logEntry.to ?? '';
          let status: ActivityEntry['status'];

          if (toState.includes('CAPTURED')) {
            status = 'captured';
          } else if (toState.includes('DECLINED')) {
            status = 'declined';
          } else if (toState.includes('REVERSED')) {
            status = 'reversed';
          } else {
            status = 'approved';
          }

          let declineReason = '';
          if (status === 'declined') {
            const logDeclineReasons = logEntry.reason?.['decline_reasons'] as
              Record<string, Record<string, unknown>> | undefined;
            declineReason = logDeclineReasons
              ? this.formatPcsDeclineReasons(logDeclineReasons)
              : this.extractDeclineReason(auth);
          }

          let entryAmount = auth.amount ?? 0;
          if (status === 'reversed' && entryAmount === 0) {
            const requestHistory = (providerData?.request_history ?? []) as Array<{
              amount?: number;
            }>;
            const firstRequest = requestHistory[0];
            if (firstRequest?.amount != null) {
              entryAmount = firstRequest.amount;
            }
          }

          entries.push({
            id: `${auth.id ?? crypto.randomUUID()}-${toState}`,
            date: logEntry.timestamp || baseDate,
            merchantName,
            referenceNumber: networkId,
            amount: entryAmount,
            status,
            declineReason,
            isLast: false,
          });
        }
      } else {
        const authState = auth.state ?? '';
        if (
          authState.includes('REQUESTED') &&
          !authState.includes('APPROVED') &&
          !authState.includes('CAPTURED') &&
          !authState.includes('DECLINED')
        ) {
          continue;
        }
        const status = this.mapAuthorizationStatus(authState);
        let declineReason = '';
        if (status === 'declined') {
          declineReason = this.extractDeclineReason(auth);
        }

        let fallbackAmount = auth.amount ?? 0;
        if (status === 'reversed' && fallbackAmount === 0) {
          const requestHistory = (providerData?.request_history ?? []) as Array<{
            amount?: number;
          }>;
          const firstRequest = requestHistory[0];
          if (firstRequest?.amount != null) {
            fallbackAmount = firstRequest.amount;
          }
        }

        entries.push({
          id: auth.id ?? crypto.randomUUID(),
          date: baseDate,
          merchantName,
          referenceNumber: networkId,
          amount: fallbackAmount,
          status,
          declineReason,
          isLast: false,
        });
      }
    }

    const transactions = card.pcsTransactions ?? [];
    for (const txn of transactions as PcsTransactionModel[]) {
      const txnState = txn.state ?? '';
      if (!txnState.includes('REFUNDED')) continue;

      const txnProviderData = txn.providerData as Record<string, unknown> | undefined;
      const txnMerchantData = txnProviderData?.merchant_data as Record<string, string> | undefined;
      const txnCreated = txnProviderData?.created;
      const txnDate =
        typeof txnCreated === 'string' && txnCreated.length > 0
          ? this.normalizeToUtc(txnCreated)
          : txn.createdAt || EPOCH_ISO;

      entries.push({
        id: txn.id ?? crypto.randomUUID(),
        date: txnDate,
        merchantName:
          txnMerchantData?.name ??
          this.intl.t('tuition_assistance.course_cash.activity.unknown_merchant'),
        referenceNumber: txnMerchantData?.network_id ?? txn.providerId ?? '',
        amount: txn.amount ?? 0,
        status: 'refunded',
        declineReason: '',
        isLast: false,
      });
    }

    const cardEvents = card.pcsCardEvents ?? [];
    const issuedEvent = (cardEvents as PcsCardEventModel[]).find(
      (evt) => evt.event === 'PCS.CardEvent.CARD_ISSUED'
    );
    const cardCreatedDate =
      issuedEvent?.createdAt ?? this.normalizeToUtc(String(card.providerData?.created ?? ''));
    if (cardCreatedDate) {
      entries.push({
        id: 'card-created',
        date: cardCreatedDate,
        merchantName: '',
        referenceNumber: '',
        amount: 0,
        status: 'created',
        declineReason: '',
        isLast: false,
      });
    }

    // Card lifecycle: deactivations and reactivations. There can be several across the
    // card's life (deactivate → reactivate → permanent deactivate), so emit one entry per
    // event; the timeline sort orders them with everything else.
    const lifecycleEvents: { event: string; status: ActivityEntry['status'] }[] = [
      { event: 'PCS.CardEvent.CARD_INACTIVATED', status: 'deactivated' },
      { event: 'PCS.CardEvent.CARD_REACTIVATED', status: 'reactivated' },
    ];
    for (const { event, status } of lifecycleEvents) {
      for (const evt of (cardEvents as PcsCardEventModel[]).filter((e) => e.event === event)) {
        if (!evt.createdAt) continue;
        entries.push({
          id: `${status}-${evt.id ?? crypto.randomUUID()}`,
          date: evt.createdAt,
          merchantName: '',
          referenceNumber: '',
          amount: 0,
          status,
          declineReason: '',
          isLast: false,
        });
      }
    }

    const sorted = entries.sort((a, b) => this.sortTimestamp(b.date) - this.sortTimestamp(a.date));
    const lastEntry = sorted[sorted.length - 1];
    if (lastEntry) {
      lastEntry.isLast = true;
    }

    return sorted;
  }

  private extractDeclineReason = (auth: PcsAuthorizationModel): string => {
    const transitionLog = auth.transitionLog;
    if (Array.isArray(transitionLog)) {
      for (const entry of transitionLog) {
        const declineReasons = entry.reason?.decline_reasons as
          Record<string, Record<string, unknown>> | undefined;
        if (declineReasons) {
          return this.formatPcsDeclineReasons(declineReasons);
        }
      }
    }

    const providerData = auth.providerData as Record<string, unknown> | undefined;
    const requestHistory = providerData?.request_history as Array<{ reason?: string }> | undefined;
    const lastEntry = requestHistory?.[requestHistory.length - 1];
    if (lastEntry?.reason && lastEntry.reason !== 'webhook_declined') {
      return this.formatStripeReason(lastEntry.reason);
    }

    return '';
  };

  private formatPcsDeclineReasons = (reasons: Record<string, Record<string, unknown>>): string => {
    const descriptions: string[] = [];

    for (const [code, detail] of Object.entries(reasons)) {
      switch (code) {
        case 'PCS.Decline.Amount.Excessive': {
          const available = (detail.card as Record<string, unknown> | undefined)?.utilization as
            Record<string, number> | undefined;
          if (available?.available != null) {
            descriptions.push(
              this.intl.t('tuition_assistance.course_cash.activity.decline_insufficient_balance', {
                amount: this.formatAmount(available.available),
              })
            );
          } else {
            descriptions.push(
              this.intl.t(
                'tuition_assistance.course_cash.activity.decline_insufficient_balance_generic'
              )
            );
          }
          break;
        }
        case 'PCS.Decline.Merchant.CategoryCode.Invalid': {
          const mcc = detail.mcc as string | undefined;
          descriptions.push(
            mcc
              ? this.intl.t('tuition_assistance.course_cash.activity.decline_merchant_category', {
                  mcc,
                })
              : this.intl.t(
                  'tuition_assistance.course_cash.activity.decline_merchant_category_generic'
                )
          );
          break;
        }
        case 'PCS.Decline.Card.NotActive':
          descriptions.push(
            this.intl.t('tuition_assistance.course_cash.activity.decline_card_inactive')
          );
          break;
        case 'PCS.Decline.External': {
          const note = detail.note as string | undefined;
          const reason = detail.reason as string | undefined;
          const label = reason ? this.formatStripeReason(reason) : null;
          const message =
            note ?? this.intl.t('tuition_assistance.course_cash.activity.decline_network');
          descriptions.push(label ? `${label}: ${message}` : message);
          break;
        }
        default:
          descriptions.push(code.replace(/^PCS\.Decline\./, '').replace(/\./g, ' — '));
      }
    }

    return descriptions.join('; ');
  };

  private formatStripeReason = (reason: string): string => {
    const keys: Record<string, string> = {
      spending_controls: 'tuition_assistance.course_cash.activity.stripe_spending_controls',
      insufficient_funds: 'tuition_assistance.course_cash.activity.stripe_insufficient_funds',
      card_inactive: 'tuition_assistance.course_cash.activity.stripe_card_inactive',
      suspected_fraud: 'tuition_assistance.course_cash.activity.stripe_suspected_fraud',
      not_allowed: 'tuition_assistance.course_cash.activity.stripe_not_allowed',
    };
    const key = keys[reason];
    return key ? this.intl.t(key) : reason.replace(/_/g, ' ');
  };

  private mapAuthorizationStatus = (state: string): ActivityEntry['status'] => {
    if (state.includes('DECLINED')) return 'declined';
    if (state.includes('CAPTURED')) return 'captured';
    if (state.includes('REVERSED')) return 'reversed';
    if (state.includes('APPROVED')) return 'approved';
    return 'approved';
  };

  private normalizeToUtc = (dateString: string) => {
    if (!dateString) return dateString;
    if (/[Zz]$/.test(dateString) || /[+-]\d{2}:\d{2}$/.test(dateString)) {
      return dateString;
    }
    return dateString + 'Z';
  };

  private sortTimestamp = (dateString: string) => {
    const timestamp = new Date(dateString).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  formatTimelineDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const datePart = this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    const timePart = this.intl.formatDate(date, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart} • ${timePart}`;
  };

  formatAmount = (amountInCents: number) => {
    return formatTransactionAmount(amountInCents, this.intl);
  };

  dotClass = (status: string) => {
    if (status === 'declined') return 'tio-bg-danger';
    if (status === 'approved') return 'tio-bg-success';
    if (status === 'captured') return 'tio-bg-info';
    if (status === 'refunded') return 'tio-bg-warning';
    if (status === 'reversed') return 'tio-bg-brand';
    if (status === 'reactivated') return 'tio-bg-success';
    if (status === 'deactivated') return 'tio-bg-warning';
    return 'tio-bg-neutral';
  };

  <template>
    <div class="tio-col-flush">
      {{#if this.isOneOfMultiple}}
        <div
          class="tio-row-between px-6 py-2.5
            {{if (eq this.resolvedCardNumber 1) '' 'border-t tio-border-default'}}
            tio-bg-sunken"
        >
          <span class="tio-text-xs-semibold tio-fg-secondary uppercase tracking-wide">
            {{t
              "tuition_assistance.course_cash.activity.card_n_of_total"
              number=this.resolvedCardNumber
              total=this.resolvedTotalCards
            }}
          </span>
          {{#if @card.providerData.last4}}
            <span class="tio-text-xs-normal tio-fg-muted">
              ••••
              {{@card.providerData.last4}}
            </span>
          {{/if}}
        </div>
      {{/if}}
      <DrawerBody>
        <div data-test-activity-status-bar class="tio-row-between py-3 mb-4">
          <div class="tio-row-tight">
            <span class="tio-text-sm-medium tio-fg-secondary">
              {{t "tuition_assistance.course_cash.activity.status_label"}}
            </span>
            <Chip @intent={{this.cardStatusIntent}} @size="sm" @appearance="faded">
              {{this.cardStatusLabel}}
            </Chip>
          </div>
          {{#if (eq this.isOneOfMultiple false)}}
            {{#if @card.providerData.last4}}
              <span class="tio-text-xs-normal tio-fg-muted">
                ••••
                {{@card.providerData.last4}}
              </span>
            {{/if}}
          {{/if}}
        </div>

        {{#if this.showReactivateButton}}
          <button
            type="button"
            data-test-reactivate-card
            class="app-course-cash-submit w-full py-3 tio-text-base-semibold mb-4"
            {{on "click" this.openReactivateConfirm}}
          >
            {{t "tuition_assistance.course_cash.activity.reactivate_card"}}
          </button>
        {{/if}}

        {{! The required reactivation reason persists in the reserve transition log and PaperTrail. }}
        <ConfirmationDialog
          @isOpen={{this.showReactivateConfirm}}
          @title={{t "tuition_assistance.course_cash.activity.reactivate_confirm_title"}}
          @intent="success"
          @confirmText={{t "tuition_assistance.course_cash.activity.reactivate_card"}}
          @confirmDisabled={{this.isReactivateCommentEmpty}}
          @isRunning={{this.handleReactivate.isRunning}}
          @error={{this.reactivateError}}
          @onConfirm={{this.handleReactivate.perform}}
          @onCancel={{this.cancelReactivateConfirm}}
        >
          <p class="mb-3">{{t
              "tuition_assistance.course_cash.activity.reactivate_confirm_body"
            }}</p>
          <dl class="tio-col-tight text-sm mb-4" data-test-reactivate-card-summary>
            {{! Top-align labels with wrapping values. }}
            <div class="flex items-start justify-between gap-6">
              <dt class="tio-fg-muted shrink-0">{{t
                  "tuition_assistance.course_cash.activity.cardholder"
                }}</dt>
              <dd class="tio-fg-primary font-medium text-right">{{this.cardholderName}}</dd>
            </div>
            <div class="flex items-start justify-between gap-6">
              <dt class="tio-fg-muted shrink-0">{{t
                  "tuition_assistance.course_cash.activity.program"
                }}</dt>
              <dd class="tio-fg-primary font-medium text-right">{{this.programName}}</dd>
            </div>
            <div class="flex items-start justify-between gap-6">
              <dt class="tio-fg-muted shrink-0">{{t
                  "tuition_assistance.course_cash.activity.status_label"
                }}</dt>
              <dd class="tio-fg-primary font-medium text-right">{{this.cardStatusLabel}}</dd>
            </div>
          </dl>
          <label class="tio-col-tight">
            <span class="tio-text-sm-medium tio-fg-secondary">
              {{t "tuition_assistance.course_cash.activity.reactivate_reason_label"}}
            </span>
            <textarea
              rows="3"
              class="app-input app-input--md w-full"
              placeholder={{t
                "tuition_assistance.course_cash.activity.reactivate_reason_placeholder"
              }}
              value={{this.reactivateComment}}
              {{on "input" this.updateReactivateComment}}
              data-test-reactivate-reason
            ></textarea>
          </label>
        </ConfirmationDialog>

        {{#if this.reactivationAudit}}
          <div
            data-test-reactivation-audit
            class="tio-row-tight tio-bg-sunken tio-fg-secondary rounded-lg px-3 py-2 mb-4 text-sm"
          >
            <PhCheckCircle @size={{16}} class="shrink-0 tio-fg-success" />
            <span>
              {{t
                "tuition_assistance.course_cash.activity.reactivated_audit"
                by=this.reactivationAudit.by
                card=this.reactivationAudit.card
                at=this.reactivationAudit.at
              }}
            </span>
          </div>
        {{/if}}

        <div data-test-activity-timeline class="tio-col-flush">
          {{#each this.activityTimeline as |entry|}}
            <div class="flex" data-test-timeline-entry>
              <div class="tio-stack-center mr-4 w-3">
                <div
                  class="w-3 h-3 rounded-full mt-1 shrink-0 {{this.dotClass entry.status}}"
                ></div>
                {{#unless entry.isLast}}
                  <div class="w-0.5 flex-1 tio-bg-divider min-h-10"></div>
                {{/unless}}
              </div>

              <div class="flex-1 pb-6">
                {{#if (eq entry.status "created")}}
                  <div class="tio-text-xs-normal tio-fg-muted">
                    {{this.formatTimelineDate entry.date}}
                  </div>
                  <div class="tio-text-sm-medium tio-fg-secondary mt-0.5">
                    {{t "tuition_assistance.course_cash.activity.card_created"}}
                  </div>
                {{else if (eq entry.status "reactivated")}}
                  <div class="tio-text-xs-normal tio-fg-muted">
                    {{this.formatTimelineDate entry.date}}
                  </div>
                  <div class="tio-text-sm-medium tio-fg-secondary mt-0.5">
                    {{t "tuition_assistance.course_cash.activity.card_reactivated"}}
                  </div>
                {{else if (eq entry.status "deactivated")}}
                  <div class="tio-text-xs-normal tio-fg-muted">
                    {{this.formatTimelineDate entry.date}}
                  </div>
                  <div class="tio-text-sm-medium tio-fg-secondary mt-0.5">
                    {{t "tuition_assistance.course_cash.activity.card_deactivated"}}
                  </div>
                {{else}}
                  <div class="tio-row-between">
                    <span class="tio-text-xs-normal tio-fg-muted">
                      {{this.formatTimelineDate entry.date}}
                    </span>
                    <span class="tio-text-sm-semibold tio-fg-primary">
                      {{this.formatAmount entry.amount}}
                    </span>
                  </div>

                  <div class="tio-row-between mt-1">
                    <div class="tio-fill-min mr-3">
                      <div class="tio-text-sm-medium tio-fg-primary truncate">
                        {{entry.merchantName}}
                      </div>
                      {{#if @showReferenceNumber}}
                        {{#if entry.referenceNumber}}
                          <div class="tio-text-micro tio-fg-muted mt-0.5">
                            {{entry.referenceNumber}}
                          </div>
                        {{/if}}
                      {{/if}}
                    </div>
                    {{#if (eq entry.status "declined")}}
                      <Chip @intent="danger" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.declined"}}
                      </Chip>
                    {{else if (eq entry.status "approved")}}
                      <Chip @intent="success" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.approved"}}
                      </Chip>
                    {{else if (eq entry.status "captured")}}
                      <Chip @intent="primary" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.captured"}}
                      </Chip>
                    {{else if (eq entry.status "refunded")}}
                      <Chip @intent="warning" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.refunded"}}
                      </Chip>
                    {{else if (eq entry.status "reversed")}}
                      <Chip @intent="default" @size="sm" @appearance="faded">
                        {{t "tuition_assistance.course_cash.activity.reversed"}}
                      </Chip>
                    {{/if}}
                  </div>

                  {{#if entry.declineReason}}
                    <div
                      class="mt-1.5 tio-text-xs-normal tio-fg-danger-body leading-snug text-pretty"
                    >
                      {{entry.declineReason}}
                    </div>
                  {{/if}}
                {{/if}}
              </div>
            </div>
          {{else}}
            <p class="tio-text-sm-normal tio-fg-muted italic py-4">
              {{t "tuition_assistance.course_cash.activity.no_activity"}}
            </p>
          {{/each}}
        </div>
      </DrawerBody>
    </div>
  </template>
}
